import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BlogCard = ({ article, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatReadTime = (minutes) => {
    return `${minutes} min read`;
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative bg-card border border-border rounded-2xl overflow-hidden shadow-3d hover:shadow-accent transition-all duration-500 transform-3d"
    >
      {/* 3D Hover Effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-success/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        animate={{
          scale: isHovered ? 1.02 : 1,
        }}
        transition={{ duration: 0.3 }}
      />
      {/* Featured Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={article?.image}
          alt={article?.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <div className="flex items-center space-x-2 bg-background/90 backdrop-blur-sm text-accent px-3 py-1.5 rounded-full text-sm font-medium">
            <Icon name={article?.category?.icon} size={14} />
            <span>{article?.category?.name}</span>
          </div>
        </div>

        {/* Reading Time */}
        <div className="absolute top-4 right-4">
          <div className="bg-background/90 backdrop-blur-sm text-muted-foreground px-3 py-1.5 rounded-full text-sm">
            <Icon name="Clock" size={14} className="inline mr-1" />
            {formatReadTime(article?.readTime)}
          </div>
        </div>

        {/* 3D Code Preview */}
        {article?.hasCodePreview && (
          <motion.div
            className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            animate={{
              y: isHovered ? [0, -5, 0] : 0,
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-2">
              <code className="text-accent text-xs font-mono">
                {article?.codeSnippet}
              </code>
            </div>
          </motion.div>
        )}
      </div>
      {/* Content */}
      <div className="p-6">
        {/* Author & Date */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="flex items-center space-x-2">
            <Image
              src={article?.author?.avatar}
              alt={article?.author?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div>
              <div className="text-sm font-medium text-foreground">
                {article?.author?.name}
              </div>
              <div className="text-xs text-muted-foreground">
                {article?.author?.role}
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {formatDate(article?.publishedAt)}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-accent transition-colors duration-300">
          {article?.title}
        </h3>

        {/* Excerpt */}
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3 leading-relaxed">
          {article?.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {article?.tags?.slice(0, 3)?.map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="px-2 py-1 bg-muted/50 text-muted-foreground text-xs rounded-md hover:bg-accent/10 hover:text-accent transition-colors duration-200"
            >
              #{tag}
            </span>
          ))}
          {article?.tags?.length > 3 && (
            <span className="px-2 py-1 text-muted-foreground text-xs">
              +{article?.tags?.length - 3} more
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Eye" size={14} />
              <span>{article?.views?.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Heart" size={14} />
              <span>{article?.likes}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="MessageCircle" size={14} />
              <span>{article?.comments}</span>
            </div>
          </div>

          <Link
            to={`/blog/${article?.slug}`}
            className="flex items-center space-x-1 text-accent hover:text-accent/80 text-sm font-medium transition-colors duration-200"
          >
            <span>Read More</span>
            <motion.div
              animate={{
                x: isHovered ? 5 : 0,
              }}
              transition={{ duration: 0.2 }}
            >
              <Icon name="ArrowRight" size={14} />
            </motion.div>
          </Link>
        </div>
      </div>
      {/* 3D Interactive Elements */}
      <motion.div
        className="absolute -top-2 -right-2 w-4 h-4 bg-accent/20 rounded-full opacity-0 group-hover:opacity-100"
        animate={{
          scale: isHovered ? [1, 1.5, 1] : 1,
          rotate: isHovered ? 360 : 0,
        }}
        transition={{ duration: 1, repeat: Infinity }}
      />
      {article?.isFeatured && (
        <div className="absolute top-0 right-0">
          <div className="bg-warning text-warning-foreground px-2 py-1 text-xs font-medium rounded-bl-lg">
            Featured
          </div>
        </div>
      )}
    </motion.article>
  );
};

export default BlogCard;